import React, { useState } from 'react';
import { Compass, HelpCircle, Smartphone, ChevronDown, ChevronUp } from 'lucide-react';
import DeviceCompass from '../DeviceCompass';

const FacingDirectionStep = ({ value, onChange }) => {
  const [showTip, setShowTip] = useState(false);
  const [showDeviceCompass, setShowDeviceCompass] = useState(false);
  const [useDeviceCompass, setUseDeviceCompass] = useState(false);

  const directions = [
    { id: 'n', label: 'N', fullName: 'North' },
    { id: 'ne', label: 'NE', fullName: 'Northeast' },
    { id: 'e', label: 'E', fullName: 'East' },
    { id: 'se', label: 'SE', fullName: 'Southeast' },
    { id: 's', label: 'S', fullName: 'South' },
    { id: 'sw', label: 'SW', fullName: 'Southwest' },
    { id: 'w', label: 'W', fullName: 'West' },
    { id: 'nw', label: 'NW', fullName: 'Northwest' }
  ];

  // Grid layout for compass
  const gridLayout = [
    ['nw', 'n', 'ne'],
    ['w', 'center', 'e'],
    ['sw', 's', 'se']
  ];

  // Handle device compass direction lock
  const handleDeviceDirection = (data) => {
    const directionMap = {
      'N': 'n', 'NE': 'ne', 'E': 'e', 'SE': 'se',
      'S': 's', 'SW': 'sw', 'W': 'w', 'NW': 'nw'
    };
    const dir = directionMap[data.direction] || 'n';
    onChange(dir);
    setUseDeviceCompass(false);
    setShowDeviceCompass(false);
  };

  return (
    <div>
      <h3 className="text-2xl font-bold text-[#232323] mb-2">
        Which direction does your main entrance face?
      </h3>
      <p className="text-[#353535] mb-6">
        Stand inside your home, facing the main door – which direction are you looking?
      </p>
      
      {/* Device Compass Toggle */}
      <button
        onClick={() => {
          setUseDeviceCompass(!useDeviceCompass);
          setShowDeviceCompass(!showDeviceCompass);
        }}
        className={`w-full mb-6 p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
          useDeviceCompass 
            ? 'border-[#232323] bg-[var(--accent-blue-200)]' 
            : 'border-dashed border-gray-300 hover:border-[#232323] hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${useDeviceCompass ? 'bg-[#232323]' : 'bg-gray-100'}`}>
            <Smartphone className={`w-5 h-5 ${useDeviceCompass ? 'text-white' : 'text-[#232323]'}`} />
          </div>
          <div className="text-left">
            <span className="font-semibold text-[#232323] block">Use Phone Compass</span>
            <span className="text-xs text-gray-500">Automatically detect direction using your device</span>
          </div>
        </div>
        {showDeviceCompass ? (
          <ChevronUp className="w-5 h-5 text-[#232323]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#232323]" />
        )}
      </button>

      {/* Device Compass Component */}
      {showDeviceCompass && (
        <div className="mb-6 animate-fadeIn">
          <DeviceCompass 
            onLockDirection={handleDeviceDirection}
            size="medium"
          />
        </div>
      )}

      {/* Manual Compass UI */}
      {!showDeviceCompass && (
        <>
          {/* Grid-based compass */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* North indicator */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
                <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[12px] border-b-[#EF4444]"></div>
                <span className="text-xs font-bold text-[#EF4444] mt-1">NORTH</span>
              </div>

              {/* Compass Grid */}
              <div className="grid grid-cols-3 gap-2 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-gray-200">
                {gridLayout.map((row, rowIndex) =>
                  row.map((cell, colIndex) => {
                    if (cell === 'center') {
                      return (
                        <div
                          key="center"
                          className="w-20 h-20 rounded-xl bg-gradient-to-br from-[var(--accent-saffron)] to-[var(--accent-orange-200)] flex items-center justify-center"
                        >
                          <Compass className="w-10 h-10 text-[#232323]" />
                        </div>
                      );
                    }
                    
                    const dir = directions.find(d => d.id === cell);
                    const isSelected = value === cell;
                    
                    return (
                      <button
                        key={cell}
                        onClick={() => onChange(cell)}
                        className={`w-20 h-20 rounded-xl border-2 font-bold text-lg transition-all hover:scale-105 active:scale-95 ${
                          isSelected
                            ? 'bg-[#232323] text-white border-[#232323] shadow-lg'
                            : 'bg-white text-[#232323] border-gray-300 hover:border-[#232323] hover:shadow-md'
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <span className="text-xl">{dir?.label}</span>
                          {isSelected && (
                            <span className="text-[10px] mt-1 opacity-80">{dir?.fullName}</span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Direction labels */}
              <div className="absolute -left-12 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">WEST</div>
              <div className="absolute -right-12 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">EAST</div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-gray-400">SOUTH</div>
            </div>
          </div>

          {/* Selected direction indicator */}
          {value && value !== 'unsure' && (
            <div className="text-center mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <span className="text-green-700 font-semibold">
                ✓ Selected: {directions.find(d => d.id === value)?.fullName || value.toUpperCase()}-facing
              </span>
            </div>
          )}
        </>
      )}

      {/* Help options */}
      <div className="mt-4 space-y-3">
        <button
          onClick={() => setShowTip(!showTip)}
          className="w-full p-4 border-2 border-[var(--accent-blue-200)] bg-[var(--accent-blue-200)] rounded-xl text-left hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <HelpCircle className="w-5 h-5 text-[#232323]" />
            <span className="font-semibold text-[#232323]">I'm not sure – help me find it</span>
          </div>
        </button>
        
        {showTip && (
          <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl animate-fadeIn">
            <p className="text-sm text-[#353535] mb-3"><strong>🧭 Quick tips:</strong></p>
            <ul className="text-sm text-[#353535] space-y-2 ml-4 list-disc">
              <li><strong>Use your phone compass</strong> - Click "Use Phone Compass" above for automatic detection</li>
              <li><strong>Sun method:</strong> The sun rises in the <strong>East</strong> and sets in the <strong>West</strong></li>
              <li><strong>Google Maps:</strong> Open Maps, face your entrance - the top of the map is North</li>
              <li><strong>Stand at entrance:</strong> Face outside from your main door - that's your facing direction</li>
            </ul>
          </div>
        )}
        
        <button
          onClick={() => onChange('unsure')}
          className={`w-full p-4 border-2 rounded-xl text-left transition-all hover:shadow-md ${
            value === 'unsure'
              ? 'border-[#232323] bg-[var(--accent-saffron)]'
              : 'border-gray-200 hover:border-[#999999]'
          }`}
        >
          <span className="font-semibold text-[#232323]">No idea / Can't tell</span>
        </button>
      </div>
    </div>
  );
};

export default FacingDirectionStep;
